/**
 * Shared option lists, labels and row-field rules for the assessment templates. The three enum-ish
 * fields (`type`, `discipline_code`, `grade`) are used by the filters, the create modal and the
 * table, so they live in one place; `title`'s bounds are here for the same reason, now that both the
 * create modal and the editor's title field enforce them. Values are the raw wire values (§0), never
 * a display string.
 */
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { AppBadgeColor } from '@shared/ui/badges'

import { AppRoute } from '@shared/config'

import { AssessmentTemplateStatus, AssessmentType, Grade } from './enums'

/** `MATH` | `ELA` — a union in lib, so there is no enum to iterate. */
export const DISCIPLINE_CODES: readonly DisciplineCode[] = ['MATH', 'ELA'] as const

const ASSESSMENT_TYPE_LABELS: Record<AssessmentType, string> = {
  [AssessmentType.Baseline]: 'Baseline',
}

const DISCIPLINE_CODE_LABELS: Record<DisciplineCode, string> = {
  MATH: 'Math',
  ELA: 'ELA',
}

const TEMPLATE_STATUS_LABELS: Record<AssessmentTemplateStatus, string> = {
  [AssessmentTemplateStatus.Draft]: 'Draft',
  [AssessmentTemplateStatus.Published]: 'Published',
  [AssessmentTemplateStatus.Archived]: 'Archived',
}

/** Same palette as the assignment templates' badge, so the two catalogues read alike. */
const TEMPLATE_STATUS_COLORS: Record<AssessmentTemplateStatus, AppBadgeColor> = {
  [AssessmentTemplateStatus.Draft]: 'slate',
  [AssessmentTemplateStatus.Published]: 'emerald',
  [AssessmentTemplateStatus.Archived]: 'orange',
}

export const getAssessmentTypeLabel = (value: AssessmentType): string =>
  ASSESSMENT_TYPE_LABELS[value] ?? value

export const getDisciplineCodeLabel = (value: DisciplineCode): string =>
  DISCIPLINE_CODE_LABELS[value] ?? value

export const getTemplateStatusLabel = (value: AssessmentTemplateStatus): string =>
  TEMPLATE_STATUS_LABELS[value] ?? value

export const getTemplateStatusColor = (value: AssessmentTemplateStatus): AppBadgeColor =>
  TEMPLATE_STATUS_COLORS[value] ?? 'slate'

/** `K4` → `Grade 4`, `K` → `Kindergarten`. The `K` prefix is the enum's own spelling. */
export const getGradeLabel = (value: Grade): string => {
  const number = String(value).replace(/^K/, '')

  return number === '' ? 'Kindergarten' : `Grade ${number}`
}

export const assessmentTypeOptions: AppSelectOption<AssessmentType>[] = Object.values(AssessmentType)
  .map(value => ({ value, text: getAssessmentTypeLabel(value) }))

export const disciplineCodeOptions: AppSelectOption<DisciplineCode>[] = DISCIPLINE_CODES
  .map(value => ({ value, text: getDisciplineCodeLabel(value) }))

export const gradeOptions: AppSelectOption<Grade>[] = Object.values(Grade)
  .map(value => ({ value, text: getGradeLabel(value) }))

/** In lifecycle order — the same order the index sorts by (Draft → Published → Archived). */
export const templateStatusOptions: AppSelectOption<AssessmentTemplateStatus>[]
  = Object.values(AssessmentTemplateStatus)
    .map(value => ({ value, text: getTemplateStatusLabel(value) }))

/** `/content/assessments/12` — the editor route (`AppRoute.AssessmentEdit` is the `[id]` pattern, which
 *  cannot be pushed as-is). Create, duplicate and the row's Edit all land here. */
export const getAssessmentEditPath = (templateId: number | string): string =>
  `${AppRoute.Assessments}/${templateId}`

/** `title` is `required|max:255` on both verbs (contract §3/§4). Shared so the create modal and the
 *  editor's title field refuse the same strings the API would, before a 422 says so. */
export const TEMPLATE_TITLE_MAX_LENGTH = 255

/** Trimmed, because " " is not a name — the API's `required` rejects it too. */
export const isTemplateTitleValid = (title: string): boolean => {
  const trimmed = title.trim()

  return trimmed.length > 0 && trimmed.length <= TEMPLATE_TITLE_MAX_LENGTH
}
